<script lang="ts">
import { defineComponent } from 'vue'

interface PrintStatusResponse {
  status?: number,
  message?: string,
  jobIdentifier?: string
}

interface PrintResult {
  success: boolean,
  message?: string
}

const SCRIPTX_LICENSE = process.env.VUE_APP_SCRIPTX_LICENSE || '8f351de0-5990-45c3-8fd2-8037b878939f'
const SCRIPTX_LICENSE_REVISION = process.env.VUE_APP_SCRIPTX_LICENSE_REVISION || 0

export default defineComponent({
  name: 'ScriptX',

  // #region Methods

  methods: {
    async callPrintService (apiPath: string, method: string, body?) {
      const response = await fetch('http://127.0.0.1:41191' + apiPath, {
        method,
        cache: 'no-cache',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Basic ' + btoa(SCRIPTX_LICENSE + ':') // license key
        },
        body: body ? JSON.stringify(body) : null
      })

      if (!response.ok) {
        throw Error(await response.text())
      }

      return await response.json()
    },

    /**
     * Validate license and initialize the ScriptX print service.
     * https://support.meadroid.com/Developers/KnowledgeBank/TechnicalReference/ScriptXServices/WebServiceAPIReference/Licensing/licensingPOST
     * @returns {boolean} whether the service is available
     */
    async initialize (): Promise<boolean> {
      try {
        await this.callPrintService('/api/v1/licensing', 'POST', {
          guid: SCRIPTX_LICENSE,
          url: 'warehouse',
          revision: SCRIPTX_LICENSE_REVISION
        })

        return true
      } catch (error) {
        return false
      }
    },

    /**
     * Get available printers
     * https://support.meadroid.com/Developers/KnowledgeBank/TechnicalReference/ScriptXServices/WebServiceAPIReference/ServiceDescription/GET
     */
    async getPrinters (): Promise<string[]> {
      const serviceResponse = await this.callPrintService('/api/', 'GET')
      return serviceResponse.availablePrinters ?? []
    },

    /**
     * ScriptX has no concept of a stored default printer; the OS default is used
     * automatically when no printerName is passed to /printHtml/print.
     */
    async getDefaultPrinter (): Promise<string | null> {
      return null
    },

    /**
     * Begin an individual print job for a single (already dimension-fixed) SVG document.
     * https://support.meadroid.com/Developers/KnowledgeBank/TechnicalReference/ScriptXServices/WebServiceAPIReference/PrintHtml/printPOST
     * @param {string} fixedSvg
     * @param {string | null} printerName
     * @param {string} [_documentName] ignored by ScriptX; present for provider contract parity
     * @returns {PrintResult}
     */
    async printOne (fixedSvg: string, printerName: string | null, _documentName?: string): Promise<PrintResult> {
      const requestBody = {
        contentType: 4, // 1 = Url, 2 = HTML page, 4 = InnerHTML
        content: fixedSvg,
        device: {
          printerName
        },
        settings: {
          header: '',
          footer: '',
          page: {
            margins: {
              top: 0,
              bottom: 0,
              left: 0,
              right: 0
            }
          }
        }
      }

      const printStatusResponse: PrintStatusResponse | undefined = await this.callPrintService('/api/v1/printHtml/print', 'POST', requestBody)

      // ScriptX status: 1 (Queued to device), 2 (Queued to file), 3 (Soft error), 4 (Ok)
      if (printStatusResponse?.status === 1 || printStatusResponse?.status === 2 || printStatusResponse?.status === 4) {
        return { success: true }
      }
      return { success: false, message: printStatusResponse?.message }
    }
  }
})
</script>

<template>
  <div />
</template>
